import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Box,
  alpha,
  Stack,
  IconButton,
  Badge,
  Skeleton
} from '@mui/material';
import {
  Warning as WarningIcon,
  Info as InfoIcon,
  ElectricBolt as ElectricIcon,
  Speed as SpeedIcon,
  ChevronLeft,
  ChevronRight,
  Business as BusinessIcon,
  Cable as CableIcon,
  CheckCircle as AvailableIcon,
  Cancel as UnavailableIcon,
  PowerSettingsNew as ConnectorIcon
} from '@mui/icons-material';
import { ChargerService } from '../../services';
import { toast } from '../../utils';
import { useSelector } from 'react-redux';
import { selectSelectedTeam } from '../../store/authSlice';

const ChargersTab = ({ onChargerDetails }) => {
  const selectedTeam = useSelector(selectSelectedTeam);
  const [loading, setLoading] = useState(true);
  const [chargersData, setChargersData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 5
  });

  const fetchChargers = useCallback(async (page = 1) => {
    if (!selectedTeam?.teamId) return;
    
    setLoading(true);
    try {
      const teamParams = {
        teamId: selectedTeam.teamId,
        teamType: selectedTeam.teamType,
        page: page,
        limit: pagination.itemsPerPage
      };
      
      const response = await ChargerService.getAllChargers(teamParams);
      
      if (response.success && response.data) {
        // Handle different possible API response structures
        let apiChargers = [];
        
        if (response.data.data && Array.isArray(response?.data?.data?.chargers)) {
          apiChargers = response?.data?.data?.chargers;
        } 
        
        
        // Use API pagination if available, otherwise paginate locally
        if (response.data.data.totalRecords) {
          // Server-side pagination
          setChargersData(apiChargers);
          setPagination(prev => ({
            ...prev,
            currentPage: page, // Use the requested page number
            totalItems: response.data.data.totalRecords,
            totalPages: Math.ceil(response.data.data.totalRecords / prev.itemsPerPage)
          }));
        } else {
          // Client-side pagination fallback
          const startIndex = (page - 1) * pagination.itemsPerPage;
          const endIndex = startIndex + pagination.itemsPerPage;
          const paginatedChargers = apiChargers.slice(startIndex, endIndex);
          
          setChargersData(paginatedChargers);
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalItems: apiChargers.length,
            totalPages: Math.ceil(apiChargers.length / prev.itemsPerPage)
          }));
        }
      } else {
        console.log('API response not successful or no data:', response);
        setChargersData([]);
        toast.error(response.message || 'Failed to load chargers');
      }
    } catch (error) {
      console.error('Error fetching chargers:', error);
      setChargersData([]);
      toast.error('Error loading chargers');
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, selectedTeam?.teamType, pagination.itemsPerPage]);

  useEffect(() => {
    if (selectedTeam?.teamId) {
      fetchChargers(1);
    } else {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, fetchChargers]);

  const handlePageChange = (newPage) => {
    fetchChargers(newPage);
  };

  const getConnectorStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return { color: '#4caf50', icon: <AvailableIcon /> };
      case 'charging':
        return { color: '#2196f3', icon: <ElectricIcon /> };
      case 'unavailable':
        return { color: '#f44336', icon: <UnavailableIcon /> };
      case 'preparing':
        return { color: '#ff9800', icon: <ConnectorIcon /> };
      case 'faulted':
        return { color: '#d32f2f', icon: <WarningIcon /> };
      case 'in use':
        return { color: '#9e9e9e', icon: <ConnectorIcon /> };
      default:
        return { color: '#9e9e9e', icon: <ConnectorIcon /> };
    }
  };

  const getOverallChargerStatus = (charger) => {
    if (!charger.connectors || charger.connectors.length === 0) return 'unknown';
    
    const connectorStatuses = charger.connectors.map(c => c.status?.toLowerCase());
    
    if (connectorStatuses.some(s => s === 'charging')) return 'charging';
    if (connectorStatuses.some(s => s === 'in use')) return 'in use';
    if (connectorStatuses.some(s => s === 'available')) return 'available';
    if (connectorStatuses.every(s => s === 'faulted')) return 'faulted';
    if (connectorStatuses.some(s => s === 'preparing')) return 'preparing';
    return 'unavailable';
  };

  const getStatusChip = (charger) => {
    const status = getOverallChargerStatus(charger);
    const config = {
      available: {
        color: 'success',
        bg: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
        icon: <ElectricIcon sx={{ fontSize: 16 }} />
      },
      charging: {
        color: 'primary',
        bg: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
        icon: <ElectricIcon sx={{ fontSize: 16 }} />
      },
      'in use': {
        color: 'warning',
        bg: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
        icon: <ConnectorIcon sx={{ fontSize: 16 }} />
      },
      faulted: {
        color: 'error',
        bg: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
        icon: <WarningIcon sx={{ fontSize: 16 }} />
      }
    };
    
    const statusConfig = config[status] || config.faulted;
    
    return (
      <Chip 
        icon={statusConfig.icon}
        label={status.charAt(0).toUpperCase() + status.slice(1)} 
        size="medium"
        sx={{
          background: statusConfig.bg,
          color: 'white',
          fontWeight: 600,
          boxShadow: `0 2px 8px ${alpha(
            statusConfig.color === 'success' ? '#4caf50' : 
            statusConfig.color === 'primary' ? '#2196f3' : 
            statusConfig.color === 'warning' ? '#ff9800' : '#f44336', 0.3
          )}`,
          '& .MuiChip-icon': {
            color: 'white'
          }
        }}
      />
    );
  };

  // Skeleton component for loading state
  const ChargerCardSkeleton = () => (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Skeleton variant="text" width={120} height={32} />
              <Skeleton variant="circular" width={20} height={20} />
            </Box>
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={150} height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={20} />
            
            {/* Connector Status Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Skeleton variant="text" width={80} height={16} sx={{ mr: 1 }} />
              <Skeleton variant="rounded" width={40} height={24} />
              <Skeleton variant="rounded" width={40} height={24} />
              <Skeleton variant="rounded" width={40} height={24} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={100} height={32} />
        </Stack>
        
        {/* Optional heartbeat section skeleton */}
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 3, 
            background: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width={120} height={20} />
            </Box>
            <Skeleton variant="text" width={160} height={16} />
          </CardContent>
        </Card>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: 2 }} />
        </Box>
      </CardContent>
    </Card>
  );

  const ChargersSkeleton = () => (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={150} height={32} />
        </Box>
      </Box>
      
      {/* Multiple charger card skeletons */}
      {[1, 2, 3, 4, 5].map((item) => (
        <ChargerCardSkeleton key={item} />
      ))}
      
      {/* Pagination skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={60} height={20} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return <ChargersSkeleton />;
  }

  // Show message if no team or company is selected
  if (!selectedTeam?.teamId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 3 }}>
        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Select a Team or Company
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please select a team or company from the dropdown above to view chargers
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header with pagination info */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="600" color="primary.main">
            Chargers ({pagination.totalItems})
          </Typography>
        </Box>
      </Box>

      {chargersData.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No chargers found for this team
          </Typography>
        </Box>
      ) : chargersData.length > 0 ? (
        chargersData.map((charger) => (
          <Card 
            key={charger.chargepoint_pk || charger.id}
            sx={{ 
              mb: 2,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography 
                      variant="h5" 
                      fontWeight="700"
                      sx={{ 
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      {charger.chargepoint_id || charger.id}
                    </Typography>
                    <Badge 
                      badgeContent={charger.connectors?.length || 0}
                      color="primary"
                      sx={{ 
                        '& .MuiBadge-badge': {
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    >
                      <CableIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    </Badge>
                  </Box>
                  <Typography variant="body1" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                    {charger.chargepoint_name || charger.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {charger.city}, {charger.state}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SpeedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      {charger.charger_type} • {charger.ocpp_protocol}
                    </Typography>
                  </Box>
                  
                  {/* Connector Status Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      Connectors:
                    </Typography>
                    {charger.connectors?.map((connector, index) => {
                      const statusConfig = getConnectorStatusColor(connector.status);
                      return (
                        <Chip
                          key={connector.connector_pk || index}
                          size="small"
                          icon={statusConfig.icon}
                          label={`${connector.connectorId}`}
                          sx={{
                            bgcolor: alpha(statusConfig.color, 0.1),
                            color: statusConfig.color,
                            borderColor: statusConfig.color,
                            border: '1px solid',
                            fontSize: '0.75rem',
                            height: 24,
                            '& .MuiChip-icon': {
                              color: statusConfig.color,
                              fontSize: 14
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
                {getStatusChip(charger)}
              </Stack>
            
            {(getOverallChargerStatus(charger) === 'charging' || getOverallChargerStatus(charger) === 'in use') && (
              <Card 
                variant="outlined" 
                sx={{ 
                  mb: 2, 
                  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(66, 165, 245, 0.05) 100%)',
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ElectricIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" color="primary.main" fontWeight={600}>
                      Last Heartbeat
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ color: 'text.secondary' }}
                  >
                    {charger.lastHeartbeatTimestamp ? 
                      new Date(charger.lastHeartbeatTimestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'No recent heartbeat'
                    }
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Button 
                variant="contained"
                size="large"
                startIcon={<InfoIcon />}
                onClick={() => onChargerDetails(charger)}
                sx={{ 
                  width: '100%',
                  height: 46,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                View Details & Controls
              </Button>
            </Box>
          </CardContent>
        </Card>
        ))
      ) : null}
      
      {/* Enhanced pagination with page numbers */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, mb: 2 }}>
      {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || loading}
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'center' }}>
              {pagination.currentPage} of {pagination.totalPages}
            </Typography>
            
            <IconButton 
              size="small"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages || loading}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
        </Box>
    </Box>
  );
};

export default ChargersTab;